import { Component, lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './store/session';
import { Shell } from './components/Shell';
import { Toaster } from './components/Toast';

const Login = lazy(() => import('./features/auth/Login').then((m) => ({ default: m.Login })));
const Dashboard = lazy(() =>
  import('./features/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const Leaderboard = lazy(() =>
  import('./features/leaderboard/Leaderboard').then((m) => ({ default: m.Leaderboard })),
);
const Leads = lazy(() => import('./features/leads/Leads').then((m) => ({ default: m.Leads })));
const Activity = lazy(() =>
  import('./features/activity/Activity').then((m) => ({ default: m.Activity })),
);
const ImportExport = lazy(() =>
  import('./features/importexport/ImportExport').then((m) => ({ default: m.ImportExport })),
);
const Admin = lazy(() => import('./features/admin/Admin').then((m) => ({ default: m.Admin })));
const ProspectQualification = lazy(() =>
  import('./features/admin/ProspectQualification').then((m) => ({ default: m.ProspectQualification })),
);
const Qualify = lazy(() =>
  import('./features/qualify/Qualify').then((m) => ({ default: m.Qualify })),
);
const Scripts = lazy(() =>
  import('./features/scripts/Scripts').then((m) => ({ default: m.Scripts })),
);
const QuickAnswers = lazy(() =>
  import('./features/quickanswers/QuickAnswers').then((m) => ({ default: m.QuickAnswers })),
);
const Duplicates = lazy(() =>
  import('./features/duplicates/Duplicates').then((m) => ({ default: m.Duplicates })),
);
const Assignment = lazy(() =>
  import('./features/assignment/Assignment').then((m) => ({ default: m.Assignment })),
);
const Team = lazy(() => import('./features/team/Team').then((m) => ({ default: m.Team })));
const RepScorecard = lazy(() =>
  import('./features/team/RepScorecard').then((m) => ({ default: m.RepScorecard })),
);
const Coaching = lazy(() =>
  import('./features/coaching/Coaching').then((m) => ({ default: m.Coaching })),
);

/** An open tab may request an older screen chunk after a new deployment. */
class PageBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="space-y-3 p-6" role="alert">
        <p>This page couldn’t load. Reload to try again.</p>
        <button className="btn-sky" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

/** Manager/admin-only route. Honors "View as Rep": while impersonating, a manager is treated as a rep. */
function ManagerRoute({ children }: { children: JSX.Element }) {
  const isManagerView = useSession((s) => s.isManagerView());
  return isManagerView ? children : <Navigate to="/" replace />;
}

function AdminRoute({ children }: { children: JSX.Element }) {
  const isAdminView = useSession((s) => s.isAdminView());
  return isAdminView ? children : <Navigate to="/" replace />;
}

export default function App() {
  const { ready, user, bootstrap } = useSession();
  const location = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center text-navy">
        <div className="animate-pulse text-lg font-semibold">Loading LeadGENOS…</div>
      </div>
    );
  }

  if (!user)
    return (
      <PageBoundary key="login">
        <Suspense fallback={<div role="status">Loading…</div>}>
          <Login />
        </Suspense>
      </PageBoundary>
    );

  return (
    <>
      <Shell>
        <PageBoundary key={location.pathname.split('/')[1]}>
          <Suspense
            fallback={
              <div className="p-6 text-greyish" role="status">
                Loading…
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/qualify" element={<Qualify />} />
              <Route path="/qualify/:leadId" element={<Qualify />} />
              <Route path="/scripts" element={<Scripts />} />
              <Route path="/scripts/:leadId" element={<Scripts />} />
              <Route path="/quick-answers" element={<QuickAnswers />} />
              <Route path="/activity" element={<Activity />} />
              <Route
                path="/admin/prospect-qualification"
                element={
                  <AdminRoute>
                    <ProspectQualification />
                  </AdminRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ManagerRoute>
                    <Leaderboard />
                  </ManagerRoute>
                }
              />
              <Route
                path="/import-export"
                element={
                  <ManagerRoute>
                    <ImportExport />
                  </ManagerRoute>
                }
              />
              <Route
                path="/assignment"
                element={
                  <ManagerRoute>
                    <Assignment />
                  </ManagerRoute>
                }
              />
              <Route
                path="/duplicates"
                element={
                  <ManagerRoute>
                    <Duplicates />
                  </ManagerRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ManagerRoute>
                    <Team />
                  </ManagerRoute>
                }
              />
              <Route
                path="/rep/:repId"
                element={
                  <ManagerRoute>
                    <RepScorecard />
                  </ManagerRoute>
                }
              />
              <Route
                path="/coaching"
                element={
                  <ManagerRoute>
                    <Coaching />
                  </ManagerRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ManagerRoute>
                    <Admin />
                  </ManagerRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </PageBoundary>
      </Shell>
      <Toaster />
    </>
  );
}
